import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

export default function Terms() {
  const [activeSection, setActiveSection] = useState('generelle-oplysninger');

  const sections = [
    { id: 'generelle-oplysninger', title: '1. Generelle oplysninger' },
    { id: 'booking-betaling', title: '2. Booking og betaling' },
    { id: 'ankomst-afrejse', title: '3. Ankomst og afrejse' },
    { id: 'afbestilling', title: '4. Afbestilling' },
    { id: 'ordensregler', title: '5. Ordensregler' },
    { id: 'ansvar-skader', title: '6. Ansvar og skader' },
    { id: 'force-majeure', title: '7. Force majeure' },
    { id: 'persondata', title: '8. Persondata' },
    { id: 'tvister', title: '9. Tvister' },
  ];

  const scrollToSection = (id) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 120;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="py-12 bg-stone-50 min-h-screen">
      <Helmet>
        <title>Forretningsbetingelser - Campen Camping</title>
        <meta name="description" content="Læs de gældende forretningsbetingelser for booking og ophold hos Campen Camping på Samsø." />
      </Helmet>

      <div className="mx-auto max-w-7xl px-6">
        {/* Header Section */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#67aedd] text-xs font-bold uppercase tracking-wider">
            Vilkår & Betingelser
          </span>
          <h1 className="text-3xl md:text-5xl font-serif font-bold text-[#0f0d0e] mt-1 mb-4">
            Forretningsbetingelser
          </h1>
          <p className="text-stone-600 text-sm md:text-base leading-relaxed">
            Seneste ændring er foretaget søndag d. 06. juni 2026.
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
          
          {/* Sidebar Navigation - Sticky on desktop */}
          <div className="lg:col-span-1">
            <div className="sticky top-32 bg-white rounded-2xl border border-stone-200/80 p-6 shadow-sm">
              <h3 className="text-stone-900 font-serif font-bold text-base mb-4 pb-3 border-b border-stone-100">
                Indholdsfortegnelse
              </h3>
              <nav className="flex flex-col gap-1">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`text-left py-2 px-3 rounded-lg text-xs md:text-sm font-medium transition-all duration-200 ${
                      activeSection === section.id
                        ? 'bg-blue-50 text-[#67aedd] pl-4 border-l-2 border-[#67aedd]'
                        : 'text-stone-600 hover:text-[#67aedd] hover:bg-stone-50'
                    }`}
                  >
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Document Content */}
          <div className="lg:col-span-3 bg-white rounded-2xl border border-stone-200/80 p-8 md:p-12 shadow-sm prose prose-stone max-w-none">
            
            {/* 1. Generelle oplysninger */}
            <section id="generelle-oplysninger" className="mb-12 scroll-mt-32">
              <h2 className="text-xl md:text-2xl font-serif font-bold text-[#0f0d0e] mb-4 pb-2 border-b border-stone-100">
                1. Generelle oplysninger
              </h2>
              <div className="text-stone-600 text-sm md:text-base space-y-4 leading-relaxed">
                <p>Campingpladsen drives af:</p>
                <div className="bg-stone-50 rounded-xl p-5 border border-stone-200/60 not-italic text-sm text-stone-700 space-y-2">
                  <div>
                    <span className="block text-xs font-semibold text-stone-500 uppercase tracking-wider">Firmanavn</span>
                    <p className="font-semibold text-stone-900">F Campen v/Finn Campen</p>
                  </div>
                  <div>
                    <span className="block text-xs font-semibold text-stone-500 uppercase tracking-wider">CVR.</span>
                    <p className="font-semibold text-stone-900">33438516</p>
                  </div>
                  <div>
                    <span className="block text-xs font-semibold text-stone-500 uppercase tracking-wider">Adresse</span>
                    <p className="font-semibold text-stone-900">Vestermarksvej 72, 8305 Samsø</p>
                  </div>
                  <div>
                    <span className="block text-xs font-semibold text-stone-500 uppercase tracking-wider">Telefon</span>
                    <p className="font-semibold text-stone-900">
                      <a href="tel:+4551540023" className="text-[#67aedd] hover:underline">+45 51540023</a>
                    </p>
                  </div>
                  <div>
                    <span className="block text-xs font-semibold text-stone-500 uppercase tracking-wider">E-mail</span>
                    <p className="font-semibold text-stone-900">
                      <a href="mailto:info@campencamping.dk" className="text-[#67aedd] hover:underline">info@campencamping.dk</a>
                    </p>
                  </div>
                </div>
                <p>
                  Disse forretningsbetingelser gælder for alle bookinger og ophold på campingpladsen. Seneste ændring er foretaget søndag d. 06. juni 2026.
                </p>
              </div>
            </section>

            {/* 2. Booking og betaling */}
            <section id="booking-betaling" className="mb-12 scroll-mt-32">
              <h2 className="text-xl md:text-2xl font-serif font-bold text-[#0f0d0e] mb-4 pb-2 border-b border-stone-100">
                2. Booking og betaling
              </h2>
              <div className="text-stone-600 text-sm md:text-base space-y-6 leading-relaxed">
                
                <div>
                  <h3 className="text-lg font-serif font-bold text-[#0f0d0e] mb-2">2.1 Booking</h3>
                  <p>
                    Booking kan foretages online, telefonisk eller via e-mail. Manglende rettidig betaling medfører annullering af booking. Booking er endelig og uigenkaldelig når vi har modtaget din betaling.
                  </p>
                </div>

                <div className="pl-4 border-l-2 border-stone-200 space-y-4">
                  <h4 className="text-base font-serif font-bold text-[#0f0d0e]">2.1.1 Forbrug</h4>
                  
                  <div>
                    <p className="font-semibold text-stone-900 mb-1">2.1.1.a Alle enheder udlejes inkl. forbrug af El</p>
                    <p>
                      Forbrug af El er inklusive i prisen for booking. Ekstraordinært højt forbrug af El vil dog blive efteropkrævet.
                    </p>
                  </div>

                  <div>
                    <p className="font-semibold text-stone-900 mb-1">2.1.1.b Alle enheder udlejes plus rengøring.</p>
                    <p>
                      Såfremt lejer ikke selv ønsker at gøre rent efter endt ophold, skal dette betales for, når enheden bookes.
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-serif font-bold text-[#0f0d0e] mb-2">2.2 Betaling</h3>
                  <p className="mb-2">
                    Man kan benytte betalingskort fra Visa eller Mastercard til betaling.
                  </p>
                  <div className="my-3">
                    <img 
                      src="https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/d026a2b2-f90d-4c19-95d2-042cbc8d93d8/image.png" 
                      alt="Visa og Mastercard logoer" 
                      className="h-10 object-contain"
                    />
                  </div>
                  <p className="mb-2">
                    Man kan kun foretage bank overførsel eller kontant betaling efter forudgående aftale.
                  </p>
                  <p>
                    Ved endt ophold betales eventuel slutafregning.
                  </p>
                </div>

                <div className="pl-4 border-l-2 border-stone-200">
                  <h4 className="text-base font-serif font-bold text-[#0f0d0e] mb-2">2.2.1 Bankoplysninger</h4>
                  <div className="bg-stone-50 rounded-xl p-4 border border-stone-200/60 text-sm">
                    <p className="font-semibold text-stone-900">Djurslands Bank</p>
                    <p className="text-stone-700">Registrerings og konto nummer: 7309 0001064003</p>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-serif font-bold text-[#0f0d0e] mb-2">2.3 Valuta</h3>
                  <p>
                    Alle priser er vist i DKK og inkl. moms.
                  </p>
                </div>

              </div>
            </section>

            {/* 3. Ankomst og afrejse */}
            <section id="ankomst-afrejse" className="mb-12 scroll-mt-32">
              <h2 className="text-xl md:text-2xl font-serif font-bold text-[#0f0d0e] mb-4 pb-2 border-b border-stone-100">
                3. Ankomst og afrejse
              </h2>
              <div className="text-stone-600 text-sm md:text-base space-y-4 leading-relaxed">
                <div className="bg-stone-50 rounded-xl p-5 border border-stone-200/60 space-y-2">
                  <p><span className="font-semibold text-stone-900">Check-ind:</span> Fra kl. 10.00</p>
                  <p><span className="font-semibold text-stone-900">Check-ud:</span> Senest kl. 15.00</p>
                </div>
                <p>
                  Pladsen skal forlades ryddet og i samme stand som ved ankomst.
                </p>
              </div>
            </section>

            {/* 4. Afbestilling */}
            <section id="afbestilling" className="mb-12 scroll-mt-32">
              <h2 className="text-xl md:text-2xl font-serif font-bold text-[#0f0d0e] mb-4 pb-2 border-b border-stone-100">
                4. Afbestilling
              </h2>
              <div className="text-stone-600 text-sm md:text-base space-y-4 leading-relaxed">
                <p>
                  Er booking betalt kan der ikke afbestilles og booking betragtes som endelig, såfremt andet ikke er aftalt skriftligt.
                </p>
              </div>
            </section>

            {/* 5. Ordensregler */}
            <section id="ordensregler" className="mb-12 scroll-mt-32">
              <h2 className="text-xl md:text-2xl font-serif font-bold text-[#0f0d0e] mb-4 pb-2 border-b border-stone-100">
                5. Ordensregler
              </h2>
              <div className="text-stone-600 text-sm md:text-base space-y-4 leading-relaxed">
                <ul className="list-disc pl-5 space-y-2">
                  <li>Der skal være ro på pladsen mellem kl. fx 23.00 – 07.00.</li>
                  <li>Kørsel på pladsen skal ske i lav hastighed.</li>
                  <li>Ingen brug af åben ild.</li>
                  <li>
                    Hund er kun tilladt efter aftale og skal føres i snor, også på badebroen. Hund må ikke være til gene for andre på pladsen, dette gælder også støj gener. Afføring skal opsamles.
                  </li>
                </ul>
                <p>
                  Overtrædelse af regler kan medføre bortvisning uden refusion.
                </p>
              </div>
            </section>

            {/* 6. Ansvar og skader */}
            <section id="ansvar-skader" className="mb-12 scroll-mt-32">
              <h2 className="text-xl md:text-2xl font-serif font-bold text-[#0f0d0e] mb-4 pb-2 border-b border-stone-100">
                6. Ansvar og skader
              </h2>
              <div className="text-stone-600 text-sm md:text-base space-y-4 leading-relaxed">
                <p>
                  Gæster opholder sig på campingpladsen på eget ansvar. Campingpladsen er ikke ansvarlig for tyveri, beskadigelse eller tab af gæsters ejendele.
                </p>
                <p>
                  Gæsten er ansvarlig for skader forvoldt på faciliteter, bygninger eller inventar og hæfter økonomisk herfor.
                </p>
              </div>
            </section>

            {/* 7. Force majeure */}
            <section id="force-majeure" className="mb-12 scroll-mt-32">
              <h2 className="text-xl md:text-2xl font-serif font-bold text-[#0f0d0e] mb-4 pb-2 border-b border-stone-100">
                7. Force majeure
              </h2>
              <div className="text-stone-600 text-sm md:text-base space-y-4 leading-relaxed">
                <p>
                  Campingpladsen kan ikke holdes ansvarlig for forhold uden for vores kontrol, herunder vejrlig, naturkatastrofer, myndighedsindgreb, strejke eller andre ekstraordinære begivenheder.
                </p>
              </div>
            </section>

            {/* 8. Persondata */}
            <section id="persondata" className="mb-12 scroll-mt-32">
              <h2 className="text-xl md:text-2xl font-serif font-bold text-[#0f0d0e] mb-4 pb-2 border-b border-stone-100">
                8. Persondata
              </h2>
              <div className="text-stone-600 text-sm md:text-base space-y-4 leading-relaxed">
                <p>
                  Vi behandler personoplysninger i overensstemmelse med gældende databeskyttelseslovgivning. Oplysninger anvendes alene til booking, kundeservice og lovpligtig registrering. Se vores <Link to="/privatlivspolitik" className="text-[#67aedd] hover:underline">privatlivspolitik</Link> for detaljer.
                </p>
              </div>
            </section>

            {/* 9. Tvister */}
            <section id="tvister" className="mb-12 scroll-mt-32">
              <h2 className="text-xl md:text-2xl font-serif font-bold text-[#0f0d0e] mb-4 pb-2 border-b border-stone-100">
                9. Tvister
              </h2>
              <div className="text-stone-600 text-sm md:text-base space-y-4 leading-relaxed">
                <p>
                  Eventuelle tvister afgøres efter dansk ret ved den relevante domstol i Danmark.
                </p>
              </div>
            </section>

          </div>
        </div>

        {/* Contact CTA Box */}
        <div className="mt-16 bg-blue-50/50 rounded-2xl border border-blue-100 p-8 md:p-12 text-center max-w-3xl mx-auto">
          <i className="fa-solid fa-circle-question text-3xl text-[#67aedd] mb-4" />
          <h3 className="text-xl md:text-2xl font-serif font-bold text-[#0f0d0e] mb-3">
            Spørgsmål til vores betingelser?
          </h3>
          <p className="text-stone-600 text-sm md:text-base mb-6 max-w-xl mx-auto">
            Hvis du har spørgsmål til vores forretningsbetingelser, betaling eller afbestillingsregler, er du altid velkommen til at kontakte os direkte.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:+4551540023"
              className="bg-white hover:bg-stone-50 text-stone-800 font-bold px-6 py-3 rounded-xl border border-stone-200 shadow-sm transition-all duration-300 text-sm flex items-center gap-2"
            >
              <i className="fa-solid fa-phone text-[#67aedd]" /> +45 51540023
            </a>
            <a
              href="mailto:info@campencamping.dk"
              className="bg-[#67aedd] hover:bg-[#5aa1cc] text-white font-bold px-6 py-3 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 text-sm flex items-center gap-2"
            >
              <i className="fa-solid fa-envelope" /> Skriv til os
            </a>
          </div>
        </div>

      </div>
    </div>
  );
}
